import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiEye, FiPackage } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { proveedoresService } from '../../services';
import { exportToCsv } from '../../utils/exportReport';

const ProveedorOrdenes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tabs para el menú inferior
  const proveedorTabs = [
    {
      id: 'detalle',
      label: 'Datos Proveedor',
      route: ''
    },
    {
      id: 'ordenes',
      label: 'Órdenes de Compra',
      route: '/ordenes'
    },
    {
      id: 'notas',
      label: 'Notas',
      route: '/notas'
    }
  ];

  useEffect(() => {
    loadOrdenes();
  }, [id]);

  const loadOrdenes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener órdenes de compra del proveedor desde la API
      const result = await proveedoresService.getOrdenesProveedor(id, {
        skip: 0,
        limit: 20
      });

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.orders || result.data?.purchase_orders || []);

        const ordenesData = dataArray.map(o => ({
          id: o.id,
          numeroOrden: o.order_number || o.purchase_order_number || `PO-${o.id}`,
          fecha: o.order_date || o.purchase_date || o.created_at,
          fechaEntrega: o.delivery_date || o.expected_delivery_date,
          estado: o.status || o.order_status || 'pending',
          total: o.total || o.total_amount || 0,
          items: (o.items || o.products || []).map(item => ({
            nombre: item.product_name || item.description || item.name,
            cantidad: item.quantity || 0,
            precio: item.unit_price || item.price || 0
          })),
          subtotal: o.subtotal || 0,
          impuestos: o.tax_amount || o.taxes || 0,
          notas: o.notes || ''
        }));

        setOrdenes(ordenesData);
      } else {
        setError(result.message || 'Error al cargar las órdenes de compra');
      }
    } catch (error) {
      console.error('Error cargando órdenes:', error);
      setError('Error de conexión al cargar las órdenes de compra');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDescargar = () => {
    if (ordenes.length === 0) {
      alert('No hay órdenes para descargar.');
      return;
    }
    const columns = [
      { label: 'N° Orden', value: (o) => o.numeroOrden },
      { label: 'Fecha', value: (o) => (o.fecha ? String(o.fecha).split('T')[0] : '') },
      { label: 'Estado', value: (o) => getEstadoLabel(o.estado) },
      { label: 'Subtotal', value: (o) => o.subtotal },
      { label: 'Total', value: (o) => o.total },
      { label: 'Notas', value: (o) => o.notas }
    ];
    const stamp = new Date().toISOString().split('T')[0];
    exportToCsv(columns, ordenes, `historial_ordenes_proveedor_${id}_${stamp}`);
  };

  const handleVerDetalle = (orden) => {
    navigate(`/system/compras/${orden.id}`);
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'completed':
      case 'received':
      case 'completada':
        return 'success';
      case 'pending':
      case 'pendiente':
        return 'warning';
      case 'cancelled':
      case 'cancelada':
        return 'danger';
      case 'partial':
      case 'parcial':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'completed':
      case 'received':
        return 'Completada';
      case 'pending':
        return 'Pendiente';
      case 'cancelled':
        return 'Cancelada';
      case 'partial':
      case 'partial_received':
        return 'Parcial';
      case 'sent':
        return 'Enviada';
      case 'confirmed':
        return 'Confirmada';
      case 'draft':
        return 'Borrador';
      default:
        return estado;
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando historial de órdenes..." />;
  }

  const totalCompras = ordenes.reduce((sum, o) => sum + o.total, 0);

  return (
    <div className="cliente-historial-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/proveedores/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">HISTORIAL DE ÓRDENES</h1>
        <button onClick={handleDescargar} className="btn btn--outline">
          <FiDownload /> Descargar
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Resumen */}
      <div className="historial-summary">
        <div className="summary-card">
          <div className="summary-label">Total de Compras</div>
          <div className="summary-value">${totalCompras.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Órdenes de Compra</div>
          <div className="summary-value">{ordenes.length}</div>
        </div>
      </div>

      {/* Lista de órdenes */}
      {ordenes.length === 0 ? (
        <div className="empty-state">
          <p>No hay órdenes de compra registradas para este proveedor</p>
        </div>
      ) : (
        <div className="historial-list">
          {ordenes.map((orden) => (
            <div key={orden.id} className="historial-card">
              <div className="historial-header">
                <div className="factura-info">
                  <strong>{orden.numeroOrden}</strong>
                  <span className="fecha">{new Date(orden.fecha).toLocaleDateString('es-CO')}</span>
                </div>
                <div className="monto-info">
                  <span className="total">${orden.total.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                  <span className={`estado ${getEstadoBadgeClass(orden.estado)}`}>
                    {getEstadoLabel(orden.estado)}
                  </span>
                </div>
              </div>

              <div className="historial-body">
                {orden.items && orden.items.length > 0 ? (
                  orden.items.map((item, index) => (
                    <div key={index} className="producto-line">
                      <span className="cantidad">{item.cantidad}x</span>
                      <span className="nombre">{item.nombre}</span>
                      <span className="precio">${item.precio.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '0.9em', color: '#6b7280', fontStyle: 'italic' }}>
                    <FiPackage style={{ marginRight: '5px' }} />
                    Sin productos detallados
                  </div>
                )}
              </div>

              <div className="historial-footer">
                <div className="subtotals">
                  <div className="subtotal-line">
                    <span>Subtotal:</span>
                    <span>${orden.subtotal.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="subtotal-line">
                    <span>Impuestos:</span>
                    <span>${orden.impuestos.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
                <button
                  className="btn btn--outline btn--small"
                  onClick={() => handleVerDetalle(orden)}
                >
                  <FiEye /> Ver Detalles
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/proveedores/${id}`} tabs={proveedorTabs} currentTab="ordenes" />
    </div>
  );
};

export default ProveedorOrdenes;
